/* BioAgeTrendChart — 12-month chronological vs biological age line chart.
 * Shown inside BiologicalAgePanel (above history) when ≥2 data points exist.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid, Legend } from "recharts";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

export default function BioAgeTrendChart({ refreshKey = 0 }) {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/biological-age/trend?months=12`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [API, refreshKey]);

  const chartData = useMemo(() => {
    if (!data?.points) return [];
    return data.points.map(p => ({
      label: p.month,
      chronological: p.chronological_age,
      biological: p.biological_age,
      gap: p.age_gap,
    }));
  }, [data]);

  if (loading) return <div className="text-xs text-gray-400" data-testid="bioage-trend-loading">{bg ? "Зареждане…" : "Loading…"}</div>;
  if (!data || (data.summary?.count || 0) < 2) return null;  // Need ≥2 points to draw a line

  const s = data.summary;
  const improving = s.improving;
  const TrendIcon = s.delta_biological_age === null || s.delta_biological_age === 0 ? Minus : (improving ? TrendingDown : TrendingUp);
  const trendColor = s.delta_biological_age === null ? "text-gray-500" : (improving ? "text-emerald-600" : "text-rose-600");

  return (
    <div className="space-y-2" data-testid="bioage-trend-chart">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp size={14} className="text-violet-600" />
          <div className="text-xs font-semibold text-gray-900">
            {bg ? "Тенденция (12 месеца)" : "Trend (12 months)"}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <TrendIcon size={12} className={trendColor} />
          <span className={`text-[11px] font-semibold ${trendColor}`} data-testid="bioage-trend-delta">
            {s.delta_biological_age > 0 ? "+" : ""}{s.delta_biological_age ?? 0} {bg ? "год." : "yrs"}
          </span>
          <span className="text-[10px] text-gray-500">
            ({s.count} {bg ? "измерв." : "pts"})
          </span>
        </div>
      </div>

      <div style={{ width: "100%", height: 180 }}>
        <ResponsiveContainer>
          <LineChart data={chartData} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "hsl(223,47%,22%)" : "#E5E7EB"} />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: isDark ? "#9CA3AF" : "#6B7280" }} />
            <YAxis tick={{ fontSize: 10, fill: isDark ? "#9CA3AF" : "#6B7280" }} domain={["auto", "auto"]} />
            <RTooltip
              contentStyle={{
                backgroundColor: isDark ? "hsl(223,47%,14%)" : "#fff",
                border: `1px solid ${isDark ? "hsl(223,47%,22%)" : "#E5E7EB"}`,
                borderRadius: 8, fontSize: 11,
              }}
              labelStyle={{ color: isDark ? "#E5E7EB" : "#1E293B", fontWeight: 600 }}
              formatter={(value, name) => {
                const labels = {
                  chronological: bg ? "Хронологична" : "Chronological",
                  biological: bg ? "Биологична" : "Biological",
                };
                return [value, labels[name] || name];
              }}
            />
            <Legend
              wrapperStyle={{ fontSize: 10, paddingTop: 4 }}
              formatter={(v) => {
                const map = {
                  chronological: bg ? "Хронологична" : "Chronological",
                  biological: bg ? "Биологична" : "Biological",
                };
                return <span style={{ color: isDark ? "#CBD5E1" : "#475569" }}>{map[v] || v}</span>;
              }}
            />
            <Line
              type="monotone" dataKey="chronological" stroke="#94A3B8" strokeWidth={2}
              strokeDasharray="5 3" dot={{ r: 3, fill: "#94A3B8" }} activeDot={{ r: 5 }}
            />
            <Line
              type="monotone" dataKey="biological" stroke="#8B5CF6" strokeWidth={2.5}
              dot={{ r: 3, fill: "#8B5CF6" }} activeDot={{ r: 5, stroke: "#fff", strokeWidth: 2 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="text-[10px] text-gray-500 italic">
        {/* iter568 — Phase 8: regulatory-softer language. Преди беше „намаляла
            с X години — продължавайте в този дух!" което е директен health claim.
            Сега е educational rewording, защитимо по EU MDR. */}
        {improving
          ? (bg
              ? `PhenoAge показва подобрение от ${Math.abs(s.delta_biological_age)} год. спрямо предходно измерване — параметрите се движат в желаната посока.`
              : `PhenoAge improved by ${Math.abs(s.delta_biological_age)} yrs vs. previous measurement — markers trending favourably.`)
          : s.delta_biological_age > 0
            ? (bg ? "PhenoAge показва покачване. Обмисли преглед на режим, хранене и сън." : "PhenoAge has risen. Consider revisiting lifestyle.")
            : (bg ? "Стабилна тенденция." : "Stable trend.")}
      </div>
    </div>
  );
}
